import { createContext, useContext, useRef, useState, useCallback } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

const ConfirmContext = createContext(() => Promise.resolve(false));

export const useConfirm = () => useContext(ConfirmContext);

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({ title: "", description: "", confirmText: "Confirm", destructive: true });
  const resolver = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts({
      title: options.title || "Are you sure?",
      description: options.description || "",
      confirmText: options.confirmText || "Confirm",
      destructive: options.destructive !== false,
    });
    setOpen(true);
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (result) => {
    setOpen(false);
    if (resolver.current) {
      resolver.current(result);
      resolver.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) close(false); }}>
        <AlertDialogContent className="rounded-none border-zinc-300" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-tight">{opts.title}</AlertDialogTitle>
            {opts.description && <AlertDialogDescription className="text-zinc-600">{opts.description}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => close(false)} data-testid="confirm-cancel" className="rounded-none border-zinc-300">Cancel</Button>
            <Button
              onClick={() => close(true)}
              data-testid="confirm-accept"
              className={`rounded-none text-white font-bold uppercase tracking-wider ${opts.destructive ? "bg-[#DC2626] hover:bg-[#B91C1C]" : "bg-primary hover:bg-[#C2410C]"}`}
            >
              {opts.confirmText}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}
